import AppButton from '@nxsys/components/AppButton';
import { defaultTheme } from '@nxsys/constants/defaultConfig';
import {
  useGetXeroSettings,
  useUpdateXeroSettings,
} from '@nxsys/hooks/api/accounting';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { Icons } from '@nxsys/types/icons';
import { showMessage } from '@nxsys/utils/message';
import { useEffect, useState } from 'react';
import {
  blankMappingKeys,
  JournalLineKey,
  NominalMapping,
} from '../../../accounting/xero/constants';
import XeroNominalMappingForm from '../../../accounting/xero/XeroNominalMappingForm';

type Props = {
  companyId?: string;
};

/**
 * NX-5235 — the company's default nominal codes for the Xero payroll journal.
 * This tab is the ONLY place the mapping is saved; the export flow reads it
 * and may override per run without persisting. Self-contained like
 * ZohoSettingsForm: owns its load + save.
 */
const XeroJournalSettingsForm: React.FC<Props> = ({ companyId }) => {
  const { messages } = useTypedIntl();
  const { run: fetchSettings, loading } = useGetXeroSettings();
  const { run: saveSettings, loading: saving } = useUpdateXeroSettings();

  // Server-owned: empty until the GET returns (inputs disabled while loading).
  const [mapping, setMapping] = useState<NominalMapping>({});
  const [missingKeys, setMissingKeys] = useState<JournalLineKey[]>([]);

  useEffect(() => {
    if (!companyId) return;
    fetchSettings(companyId)
      .then((settings) => {
        setMapping(settings.mapping);
        setMissingKeys([]);
      })
      .catch(() => undefined); // toast raised by the hook
  }, [companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async () => {
    if (!companyId) return;
    const blank = blankMappingKeys(mapping);
    setMissingKeys(blank);
    if (blank.length) {
      showMessage({
        type: 'error',
        content: messages['accounting.xeroJournal.missingCodesAll'],
      });
      return;
    }
    try {
      const saved = await saveSettings(companyId, { mapping });
      setMapping(saved.mapping);
      showMessage({
        type: 'success',
        content: messages['common.updateSuccess'],
      });
    } catch {
      // toast raised by the hook
    }
  };

  return (
    <div className="flex flex-col gap-4 max-w-[900px]">
      <XeroNominalMappingForm
        value={mapping}
        onChange={(m) => {
          setMapping(m);
          if (missingKeys.length) setMissingKeys(blankMappingKeys(m));
        }}
        missingKeys={missingKeys}
        disabled={loading}
      />

      <div className="flex items-center justify-start gap-3 pt-2">
        <AppButton
          type="primary"
          name={messages['common.save']}
          leftIcon={
            <Icons.IconCheckCircle color={defaultTheme.theme.palette.white} />
          }
          onClick={handleSave}
          loading={saving}
          disabled={loading || !companyId}
        />
      </div>
    </div>
  );
};

export default XeroJournalSettingsForm;
